import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { Menu, X, Shield, MessageCircle } from "lucide-react";
import { publicApi } from "@/lib/api";

let _settings = null;
export const useCommercial = () => {
  const [s, setS] = useState(_settings);
  useEffect(() => { if (!_settings) publicApi.get("/public/settings").then((r) => { _settings = r.data; setS(r.data); }).catch(() => {}); }, []);
  return s || {};
};

export const trackEvent = (event, props = {}) => {
  let anon = localStorage.getItem("pg_anon");
  if (!anon) { anon = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("pg_anon", anon); }
  publicApi.post("/public/events", { event, anon_id: anon, props }).catch(() => {});
};

export const useSeo = (title, description) => {
  useEffect(() => {
    document.title = title ? `${title} | Portal Gestão` : "Portal Gestão — Sistema de gestão para restaurantes";
    const m = document.querySelector('meta[name="description"]');
    if (m && description) m.setAttribute("content", description);
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.rel = "canonical"; document.head.appendChild(c); }
    c.href = window.location.origin + window.location.pathname;
  }, [title, description]);
};

export const waLink = (number, text) => number ? `https://wa.me/${number.replace(/\D/g, "")}?text=${encodeURIComponent(text)}` : null;

export const WhatsAppButton = ({ text = "Olá! Quero conhecer o Portal Gestão para meu restaurante.", label = "Falar com especialista", variant = "outline", className = "", testid = "wa-btn" }) => {
  const s = useCommercial();
  const href = waLink(s.whatsapp_number, text);
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noreferrer" onClick={() => trackEvent("landing_cta_clicked", { cta: "whatsapp" })} data-testid={testid}>
      <Button variant={variant} className={`gap-2 ${className}`}><MessageCircle className="h-4 w-4" /> {label}</Button>
    </a>
  );
};

const NAV = [
  { to: "/", label: "Início" }, { to: "/funcionalidades", label: "Funcionalidades" }, { to: "/planos", label: "Planos" },
  { to: "/#como-funciona", label: "Como funciona" }, { to: "/blog", label: "Blog" }, { to: "/#faq", label: "FAQ" },
];

export const MarketingHeader = () => {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <header className="border-b border-stone-200/70 bg-white/80 backdrop-blur-md sticky top-0 z-40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between py-3 sm:py-4">
        <Link to="/" aria-label="Portal Gestão"><BrandLogo /></Link>
        <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-stone-600">
          {NAV.map((n) => <Link key={n.to} to={n.to} className="hover:text-stone-900">{n.label}</Link>)}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/entrar" className="hidden sm:block"><Button variant="ghost" data-testid="nav-login">Entrar</Button></Link>
          <Link to="/comecar" onClick={() => trackEvent("landing_cta_clicked", { cta: "header" })}><Button className="bg-orange-600 hover:bg-orange-700" data-testid="nav-start">Teste grátis</Button></Link>
          <button className="lg:hidden p-2 rounded-md hover:bg-stone-100" aria-label="Abrir menu" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-stone-200 bg-white px-4 py-3 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {NAV.map((n) => <Link key={n.to} to={n.to} className="py-2.5 px-2 rounded-md text-stone-700 hover:bg-stone-50 font-medium">{n.label}</Link>)}
          <Link to="/entrar" className="py-2.5 px-2 rounded-md text-stone-700 hover:bg-stone-50 font-medium">Entrar</Link>
        </div>
      )}
    </header>
  );
};

export const MarketingFooter = () => {
  const s = useCommercial();
  return (
    <footer className="border-t border-stone-200 bg-white">
      <div className="mx-auto max-w-7xl px-6 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-sm">
        <div><BrandLogo size="sm" /><p className="mt-3 text-stone-500 max-w-xs">Gestão completa para restaurantes, lanchonetes, hamburguerias, pizzarias, marmitarias e delivery.</p></div>
        <div><div className="font-bold text-stone-900 mb-3">Produto</div><ul className="space-y-2 text-stone-600">
          <li><Link to="/funcionalidades">Funcionalidades</Link></li><li><Link to="/planos">Planos e preços</Link></li><li><Link to="/raio-x">Raio-X do restaurante</Link></li><li><Link to="/blog">Blog</Link></li></ul></div>
        <div><div className="font-bold text-stone-900 mb-3">Segmentos</div><ul className="space-y-2 text-stone-600">
          <li><Link to="/hamburgueria">Hamburgueria</Link></li><li><Link to="/pizzaria">Pizzaria</Link></li><li><Link to="/restaurante">Restaurante</Link></li><li><Link to="/lanchonete">Lanchonete</Link></li><li><Link to="/marmitaria">Marmitaria</Link></li><li><Link to="/delivery">Delivery</Link></li></ul></div>
        <div><div className="font-bold text-stone-900 mb-3">Suporte</div><ul className="space-y-2 text-stone-600">
          <li><Link to="/contato">Contato</Link></li><li><Link to="/#faq">Perguntas frequentes</Link></li>
          {s.contact_email && <li><a href={`mailto:${s.contact_email}`}>{s.contact_email}</a></li>}
          <li><Link to="/admin-master/login" className="inline-flex items-center gap-1.5 text-stone-400 hover:text-stone-700" data-testid="footer-admin-link"><Shield className="h-3.5 w-3.5" /> Acesso Admin Master</Link></li></ul></div>
      </div>
      <div className="border-t border-stone-100 py-4 text-center text-xs text-stone-400">© {new Date().getFullYear()} Portal Gestão. Todos os direitos reservados.</div>
    </footer>
  );
};

export const MarketingLayout = ({ children }) => (
  <div className="min-h-screen bg-[#FAF9F5] text-stone-900">
    <MarketingHeader />
    {children}
    <MarketingFooter />
  </div>
);
